import apiClient, { refreshAccessToken as refreshManagedAccessToken } from "./apiClient";
import axios from "./axiosConfig";
import axiosLib from "axios";
import API_URL from "./apiConfig";

const API_KEY =  process.env.REACT_APP_AWS_API_KEY;

const authAxios = axiosLib.create({
  baseURL: API_URL,
  withCredentials: true,
  headers: {
    "Content-Type": "application/json",
    "x-api-key": API_KEY,
  },
});

authAxios.interceptors.request.use(
  (config) => {
    config.headers = config.headers || {};
    config.headers["x-api-key"] = API_KEY;
    return config;
  },
  (error) => Promise.reject(error)
);

const keepAllowedFields = (source, allowedFields) => {
  const data = {};

  allowedFields.forEach((field) => {
    const value = source?.[field];
    if (value !== undefined && value !== null) {
      data[field] = value;
    }
  });

  return data;
};


const extractPayload = (response) => response.data?.data ?? response.data;


const normalizeIdFields = (item) => {
  if (!item || typeof item !== "object" || Array.isArray(item)) return item;

  const id = item.id || item._id;
  if (!id) return item;

  return {
    ...item,
    id,
    _id: item._id || id,
  };
};

const normalizeFormResponse = (response) => {
  const payload = normalizeIdFields(extractPayload(response));
  response.data = {
    ...response.data,
    data: payload,
    form: payload,
  };

  return response;
};

const USER_PROFILE_FIELDS = [
  "firstName",
  "lastName",
  "birthday",
  "email",
  "district",
  "image",
  "phoneNumber",
];

export const register = async (userData) => {
  const data = keepAllowedFields(userData, [
    "firstName",
    "lastName",
    "email",
    "phoneNumber",
    "subscribe",
    "promotion",
    "district",
    "image",
    "birthday",
    "gender",
  ]);

  return authAxios.post("/auth/registrations/user", data, { withCredentials: true });
};

export const registerbyPhoneNumber = async (userData) => register(userData);

export const registerbyEmail = async (userData) => register(userData);

export const generateSmsCode = async (phoneNumber) => {
  return authAxios.post("/auth/challenges", { phoneNumber }, { withCredentials: false });
};

export const VerifySmsCode = async (phoneNumber, code) => {
  return authAxios.post("/auth/challenges/verify", { phoneNumber, code }, { withCredentials: true });
};

export const generateEmailCode = async (email) => {
  return authAxios.post("/auth/challenges", { email }, { withCredentials: false });
};

export const VerifyEmailCode = async (email, resetCode) => {
  return authAxios.post("/auth/challenges/verify", { email, code: resetCode }, { withCredentials: true });
};

export const getUserProfile = async () => {
  const response = await apiClient.get("/user/me", { withCredentials: false });
  const user = response.data?.data || response.data?.user || response.data;

  response.data = {
    ...response.data,
    data: user,
    user,
  };

  return response;
};

export const refreshAccessToken = async () => refreshManagedAccessToken();

export const updateUserProfile = async (userData) => {
  const response = await axios.patch("/user/me", keepAllowedFields(userData, USER_PROFILE_FIELDS));
  return normalizeFormResponse(response);
};

export const getPetBasicInfo = async (petId) => {
  const response = await axios.get(`/pet/profile/${petId}?view=basic`);
  return normalizeFormResponse(response);
};

// Hotel endpoints (user)

export const createHotelDraft = async (initialData = {}) => {
  return apiClient.post("/hotels", initialData);
};

export const listUserHotels = async () => {
  return apiClient.get("/hotels/mine");
};

export const getHotelDraft = async (hotelId) => {
  return apiClient.get(`/hotels/${hotelId}`);
};

export const saveHotelStep = async (hotelId, stepKey, stepData) => {

  // Steps that need to be wrapped with their step key
  const stepsNeedingWrap = ["animalTypes", "services", "rooms", "roomAmenities", "promotions", "additionalServices", "bookingTerms", "payment", "billingAddress", "bookingConfirmationMethod"];
  const body = stepsNeedingWrap.includes(stepKey) ? { [stepKey]: stepData } : stepData;
  return apiClient.patch(`/hotels/${hotelId}/steps/${stepKey}`, body);
};

export const submitHotel = async (hotelId) => {
  return apiClient.post(`/hotels/${hotelId}/submit`);
};

export const getHotelStatus = async (hotelId) => {
  return apiClient.get(`/hotels/${hotelId}/status`);
};

export const uploadHotelDocument = async (hotelId, file, type, roomId = null) => {
  const formData = new FormData();
  formData.append("file", file);
  formData.append("type", type);
  if (roomId) formData.append("roomId", roomId);

  return apiClient.post(`/hotels/${hotelId}/documents`, formData, {
    headers: { "Content-Type": "multipart/form-data" },
  });
};

export const deleteHotelDocument = async (hotelId, documentId) => {
  return apiClient.delete(`/hotels/${hotelId}/documents/${documentId}`);
};

export const getHotelDocuments = async (hotelId) => {
  return apiClient.get(`/hotels/${hotelId}/documents`);
};

// Hotel endpoints (admin)

export const listSubmittedHotels = async (page = 1, limit = 10, status = "submitted") => {
  return apiClient.get("/admin/hotels", { params: { status, page, limit } });
};

export const getHotelForReview = async (hotelId) => {
  return apiClient.get(`/admin/hotels/${hotelId}`);
};

export const approveHotel = async (hotelId) => {
  return apiClient.post(`/admin/hotels/${hotelId}/approve`);
};

export const rejectHotel = async (hotelId, reason) => {
  return apiClient.post(`/admin/hotels/${hotelId}/reject`, { reason });
};

// Room endpoints

export const createRoom = async (hotelId) => {
  return apiClient.post("/rooms", { hotelId });
};

export const getRoom = async (roomId) => {
  return apiClient.get(`/rooms/${roomId}`);
};

export const updateRoomStep = async (roomId, stepKey, stepData) => {
  return apiClient.patch(`/rooms/${roomId}/steps/${stepKey}`, stepData);
};


export const updatePublishedRoomInfo = async (roomId, publishedRoomId, data) => {
  return apiClient.patch(`/rooms/${roomId}/published/${publishedRoomId}`, data);
};

export const updateRoomStatus = async (roomId, status) => {
  return apiClient.patch(`/rooms/${roomId}/${status}`);
};

export const duplicateRoom = async (roomId) => {
  return apiClient.post(`/rooms/${roomId}/duplicate`, {});
};

export const deleteRoom = async (roomId) => {
  return apiClient.delete(`/rooms/${roomId}`);
};

export const getHotelRooms = async (hotelId, status = null) => {
  const params = status ? { status } : {};
  return apiClient.get(`/rooms/hotel/${hotelId}`, { params });
};

export const createPublishedRooms = async (roomId) => {
  return apiClient.post("/rooms/create/published", { roomId });
};

export const getPublishedRooms = async (hotelId, filters = {}) => {
  return apiClient.get(`/hotels/${hotelId}/published-rooms`, { params: filters });
};

export const ListRoomBookings = async (roomId, params = {}) => {
  return apiClient.get(`/rooms/${roomId}/bookings`, { params });
};

export const updateBookingStep = async (bookingId, stepKey, stepData) => {
  return apiClient.patch(`/rooms/bookings/${bookingId}/${stepKey}`, stepData);
};

export const getReviewDocument = async (bookingId) => {
  return apiClient.get(`/rooms/bookings/${bookingId}/document`);
};


